<template>
  <div v-if="open" class="ccm-overlay" @click="onOverlayClick">
    <div
      class="ccm-shell"
      role="dialog"
      aria-modal="true"
      :aria-label="title || 'Loans'"
      @click.stop
    >
      <div class="ccm-titlebar">
        <div class="ccm-titleblock">
          <div class="ccm-title">{{ title }}</div>
          <div v-if="subtitle" class="ccm-subtitle">{{ subtitle }}</div>
        </div>

        <button class="ccm-close" type="button" @click="emitClose" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="ccm-body">
        <div class="cc-modal-toolbar">
          <div class="cc-modal-meta">
            <div class="cc-modal-meta-top">
              <span class="cc-modal-count">{{ filtered.length }} loans</span>
              <span v-if="subtitle" class="cc-modal-subtitle">{{ subtitle }}</span>
            </div>
          </div>

          <div class="cc-modal-search-wrap">
            <i class="fa-duotone fa-magnifying-glass"></i>
            <input
              v-model="search"
              class="cc-modal-search"
              type="text"
              placeholder="Search loan number or borrower..."
            />
            <button
              v-if="search"
              class="cc-modal-clear"
              type="button"
              @click="search = ''"
              title="Clear search"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        <div class="cc-modal-grid">
          <div
            v-for="loan in filtered"
            :key="loan.id"
            class="cc-loan-card"
            @click="openLoan(loan)"
            :title="`Open Loan ${loan.f364 || ''}`"
          >
            <div class="cc-loan-card__top">
              <div class="cc-loan-card__titleline">
                <div class="cc-loan-card__num">
                  <i class="fa-duotone fa-file-invoice"></i>
                  {{ loan.f364 || "Loan" }}
                </div>

                <div class="cc-loan-card__borrower">
                  {{ loan.name || "—" }}
                </div>
              </div>

              <div class="cc-loan-card__time" v-if="bestDate(loan)">
                {{ formatDate(bestDate(loan)) }}
              </div>
            </div>

            <div class="cc-loan-card__meta">
              <span v-if="loan.f2" class="cc-chip cc-chip--money">
                <i class="fa-duotone fa-sack-dollar"></i>
                {{ loan.f2 }}
              </span>

              <span v-if="loan.f3" class="cc-chip cc-chip--rate">
                <i class="fa-duotone fa-percent"></i>
                {{ loan.f3 }}
              </span>

              <span v-if="loan.f2149" class="cc-chip cc-chip--soft">
                <i class="fa-duotone fa-calendar-clock"></i>
                {{ formatDate(loan.f2149) }}
              </span>
            </div>
          </div>

          <div v-if="!filtered.length" class="cc-modal-empty">
            No loans found
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate } from "@/helpers/formatters.js"

export default {
  name: "LoanListModal",
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    loans: { type: Array, default: () => [] },
    closeOnOverlay: { type: Boolean, default: true }
  },
  data() {
    return {
      search: ""
    }
  },
  computed: {
    filtered() {
      const q = (this.search || "").trim().toLowerCase()
      if (!q) return this.loans
      return this.loans.filter((x) => {
        const ln = (x.f364 || "").toLowerCase()
        const nm = (x.name || "").toLowerCase()
        return ln.includes(q) || nm.includes(q)
      })
    }
  },
  watch: {
    open: {
      immediate: true,
      handler(v) {
        if (v) this._bindEsc()
        else this._unbindEsc()
      }
    }
  },
  beforeUnmount() {
    this._unbindEsc()
  },
  methods: {
    formatDate,
    emitClose() {
      this.$emit("close")
    },
    onOverlayClick() {
      if (this.closeOnOverlay) this.emitClose()
    },
    openLoan(loan) {
      this.$emit("open-loan", loan)
    },
    bestDate(loan) {
      return loan?.cust17fv || loan?.f2151 || loan?.f2149 || null
    },
    _bindEsc() {
      window.addEventListener("keydown", this._onKeydown, { passive: true })
    },
    _unbindEsc() {
      window.removeEventListener("keydown", this._onKeydown)
    },
    _onKeydown(e) {
      if (!this.open) return
      if (e.key === "Escape") this.emitClose()
    }
  }
}
</script>

<style scoped>
.ccm-overlay {
  position: fixed;
  inset: 0;
  z-index: 30000;
  background: rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.ccm-shell {
  width: min(980px, calc(100vw - 24px));
  max-height: min(86vh, 860px);
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 16px;
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.ccm-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(255, 255, 255, 0.92);
}

.ccm-titleblock {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ccm-title {
  font-weight: 950;
  font-size: 14px;
  color: #0f172a;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccm-subtitle {
  font-size: 12px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.55);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccm-close {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(15, 23, 42, 0.04);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(15, 23, 42, 0.70);
  transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
}

.ccm-close:hover {
  transform: translateY(-1px);
  background: rgba(40, 110, 255, 0.08);
  border-color: rgba(40, 110, 255, 0.20);
}

.ccm-close:active {
  transform: translateY(0px);
}

.ccm-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 12px;
}

/* reuse your existing styles, but make the grid scroll inside the modal */
.cc-modal-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 420px;
}

/* existing toolbar/grid styles (your original) */
.cc-modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 10px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.05);
}

.cc-modal-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.cc-modal-meta-top {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cc-modal-count {
  font-weight: 950;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(40, 110, 255, 0.10);
  border: 1px solid rgba(40, 110, 255, 0.18);
  color: rgba(29, 78, 216, 1);
  white-space: nowrap;
}

.cc-modal-subtitle {
  font-size: 12px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.55);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-modal-search-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 36px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.7);
  color: rgba(40, 110, 255, 0.9);
  min-width: 320px;
}

.cc-modal-search {
  height: 34px;
  border: none;
  background: transparent;
  outline: none;
  font-weight: 850;
  font-size: 12px;
  flex: 1;
  color: rgba(15, 23, 42, 0.82);
  min-width: 0;
}

.cc-modal-search::placeholder {
  color: rgba(15, 23, 42, 0.45);
  font-weight: 850;
}

.cc-modal-clear {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(15, 23, 42, 0.04);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(15, 23, 42, 0.65);
  transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
}

.cc-modal-clear:hover {
  transform: translateY(-1px);
  background: rgba(40, 110, 255, 0.08);
  border-color: rgba(40, 110, 255, 0.20);
}

.cc-modal-grid {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.35);
  padding: 10px;
  padding-top: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cc-loan-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 10px 10px;
  background: rgba(5, 29, 58, 0.02);
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}

.cc-loan-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  border-color: rgba(40, 110, 255, 0.22);
}

.cc-loan-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.cc-loan-card__titleline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.cc-loan-card__num {
  font-weight: 950;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.cc-loan-card__borrower {
  font-weight: 900;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.92);
  opacity: 0.9;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-loan-card__time {
  font-weight: 850;
  font-size: 11px;
  opacity: 0.75;
  white-space: nowrap;
}

.cc-loan-card__meta {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.7);
  font-size: 10.5px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.82);
}

.cc-loan-card__num i.fa-duotone {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.30);
}

.cc-chip--money i.fa-duotone {
  --fa-primary-color: rgba(10, 170, 120, 0.95);
  --fa-secondary-color: rgba(10, 170, 120, 0.30);
}

.cc-chip--rate i.fa-duotone {
  --fa-primary-color: rgba(140, 90, 255, 0.95);
  --fa-secondary-color: rgba(140, 90, 255, 0.30);
}

.cc-chip--soft i.fa-duotone {
  --fa-primary-color: rgba(15, 23, 42, 0.70);
  --fa-secondary-color: rgba(15, 23, 42, 0.22);
}

.cc-chip--money {
  border-color: rgba(10, 170, 120, 0.25);
  background: rgba(10, 170, 120, 0.06);
}

.cc-chip--rate {
  border-color: rgba(140, 90, 255, 0.25);
  background: rgba(140, 90, 255, 0.06);
}

.cc-modal-empty {
  padding: 26px 20px;
  text-align: center;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.55);
}

/* dark mode */
.cgsdark .ccm-shell {
  background: rgba(2, 6, 23, 0.74);
  border-color: rgba(148, 163, 184, 0.16);
}

.cgsdark .ccm-titlebar {
  background: rgba(2, 6, 23, 0.70);
  border-bottom-color: rgba(148, 163, 184, 0.14);
}

.cgsdark .ccm-title {
  color: rgba(226, 232, 240, 0.92);
}

.cgsdark .ccm-subtitle {
  color: rgba(226, 232, 240, 0.72);
}

.cgsdark .ccm-close {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.78);
}

.cgsdark .cc-modal-toolbar {
  border-color: rgba(148, 163, 184, 0.14);
  background: rgba(2, 6, 23, 0.58);
}

.cgsdark .cc-modal-count {
  background: rgba(59, 130, 246, 0.16);
  border-color: rgba(59, 130, 246, 0.22);
  color: rgba(96, 165, 250, 0.98);
}

.cgsdark .cc-modal-subtitle {
  color: rgba(226, 232, 240, 0.70);
}

.cgsdark .cc-modal-search-wrap {
  border-color: rgba(148, 163, 184, 0.18);
  background: rgba(148, 163, 184, 0.08);
}

.cgsdark .cc-modal-search {
  color: rgba(226, 232, 240, 0.90);
}

.cgsdark .cc-modal-search::placeholder {
  color: rgba(226, 232, 240, 0.55);
}

.cgsdark .cc-modal-grid {
  border-color: rgba(148, 163, 184, 0.14);
  background: rgba(148, 163, 184, 0.06);
}

.cgsdark .cc-loan-card {
  border-color: rgba(148, 163, 184, 0.14);
  background: rgba(148, 163, 184, 0.06);
}

.cgsdark .cc-loan-card__borrower {
  color: rgba(226, 232, 240, 0.92);
}

.cgsdark .cc-loan-card__time {
  color: rgba(226, 232, 240, 0.72);
}

.cgsdark .cc-chip {
  border-color: rgba(148, 163, 184, 0.18);
  background: rgba(2, 6, 23, 0.40);
  color: rgba(226, 232, 240, 0.86);
}

.cgsdark .cc-modal-empty {
  color: rgba(226, 232, 240, 0.70);
}
</style>
